import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { GitBranch, Code } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EditorView = 'visual' | 'code';

interface ViewToggleProps {
  activeView: EditorView;
  onViewChange: (view: EditorView) => void;
  disabled?: boolean;
  hasCodeErrors?: boolean;
}

export function ViewToggle({
  activeView,
  onViewChange,
  disabled = false,
  hasCodeErrors = false
}: ViewToggleProps) {
  const handleValueChange = (value: string) => {
    if (value && !disabled) {
      onViewChange(value as EditorView);
    }
  };

  return (
    <ToggleGroup
      type="single"
      value={activeView}
      onValueChange={handleValueChange}
      className="bg-muted rounded-md p-0.5"
    >
      <ToggleGroupItem
        value="visual"
        className={cn(
          "px-2.5 py-1 gap-1.5 text-xs rounded-sm",
          "data-[state=on]:bg-background data-[state=on]:shadow-sm",
          "hover:bg-background/50"
        )}
        disabled={disabled}
        title="Visual Editor"
      >
        <GitBranch className="h-3.5 w-3.5" />
        <span>Visual</span>
      </ToggleGroupItem>
      <ToggleGroupItem
        value="code"
        className={cn(
          "px-2.5 py-1 gap-1.5 text-xs rounded-sm",
          "data-[state=on]:bg-background data-[state=on]:shadow-sm",
          "hover:bg-background/50",
          hasCodeErrors && activeView === 'code' && "text-destructive"
        )}
        disabled={disabled}
        title="JSON Code Editor"
      >
        <Code className="h-3.5 w-3.5" />
        <span>Code</span>
        {hasCodeErrors && activeView === 'code' && (
          <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
        )}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
